/**
 * Helpers to merge outline + section-metadata proposals into one Accept payload.
 * Section updates are field-scoped so status Accept never clears word_count
 * (and vice versa).
 */

import type { OutlineProposalChange } from "@/agents-v2/api/outlineProposal";

export const OUTLINE_PROPOSAL_TOOL_NAME = "suggest_outline_changes";
export const BID_METADATA_PROPOSAL_TOOL_NAME = "suggest_bid_metadata_updates";
import {
  asArray,
  asRecord,
  getFirstStringValue,
  parseMaybeJson
} from "@/agents-v2/utils/unknown";

export type OutlineProposal = {
  summary: string;
  changes: OutlineProposalChange[];
  bidId: string | null;
  proposalId: string | null;
  rationale: string | null;
  toolCallId?: string;
};

export type BidMetadataUpdate = {
  target: "bid" | "section";
  field: string;
  value: unknown;
  section_id?: string | null;
  label?: string | null;
};

export type BidMetadataProposal = {
  summary: string;
  updates: BidMetadataUpdate[];
  bidId: string | null;
  proposalId: string | null;
  rationale: string | null;
  toolCallId?: string;
};

export function parseOutlineProposal(result: unknown): OutlineProposal | null {
  const record = asRecord(parseMaybeJson(result));
  if (!record) return null;
  if (getFirstStringValue(record, ["kind"]) !== "outline_changes") return null;
  if (getFirstStringValue(record, ["status"]) !== "success") return null;

  const changes = asArray(record.changes).filter((item) => {
    const row = asRecord(item);
    return Boolean(row && typeof row.op === "string");
  }) as OutlineProposalChange[];
  if (changes.length === 0) return null;

  return {
    summary:
      getFirstStringValue(record, ["summary"]) ||
      `${changes.length} outline change(s)`,
    changes,
    bidId: getFirstStringValue(record, ["bid_id"]),
    proposalId: getFirstStringValue(record, ["proposal_id"]),
    rationale: getFirstStringValue(record, ["rationale"])
  };
}

export function parseBidMetadataProposal(
  result: unknown
): BidMetadataProposal | null {
  const record = asRecord(parseMaybeJson(result));
  if (!record) return null;
  if (getFirstStringValue(record, ["kind"]) !== "bid_metadata_updates") {
    return null;
  }
  if (getFirstStringValue(record, ["status"]) !== "success") return null;

  const updates: BidMetadataUpdate[] = [];
  for (const item of asArray(record.updates)) {
    const row = asRecord(item);
    if (!row) continue;
    const target = getFirstStringValue(row, ["target"]);
    const field = getFirstStringValue(row, ["field"]);
    if (target !== "bid" && target !== "section") continue;
    if (!field) continue;
    updates.push({
      target,
      field,
      value: row.value,
      section_id: getFirstStringValue(row, ["section_id"]),
      label: getFirstStringValue(row, ["label"])
    });
  }
  if (updates.length === 0) return null;

  return {
    summary:
      getFirstStringValue(record, ["summary"]) ||
      `${updates.length} bid update(s)`,
    updates,
    bidId: getFirstStringValue(record, ["bid_id"]),
    proposalId: getFirstStringValue(record, ["proposal_id"]),
    rationale: getFirstStringValue(record, ["rationale"])
  };
}

/**
 * Convert section metadata updates to outline update ops.
 * Only the named field is set — never a full section replace — so word_count
 * and other fields stay untouched unless explicitly included.
 * @param updates - Metadata proposal updates from ``suggest_bid_metadata_updates``
 * @returns Outline ``update`` ops for section-targeted fields only
 */
export function metadataUpdatesToOutlineChanges(
  updates: BidMetadataUpdate[]
): OutlineProposalChange[] {
  return updates
    .filter(
      (update): update is BidMetadataUpdate & { section_id: string } =>
        update.target === "section" &&
        typeof update.section_id === "string" &&
        update.section_id.trim().length > 0 &&
        Boolean(update.field)
    )
    .map((update) => ({
      op: "update" as const,
      section_id: update.section_id,
      section: { [update.field]: update.value }
    }));
}

/**
 * Merge multiple ``update`` ops for the same section_id by combining only the
 * fields each op sets (shallow). Non-update ops stay in order.
 * @param changes - Outline proposal ops to coalesce
 * @returns Changes with same-section field updates merged into one op each
 */
export function coalesceSectionUpdateChanges(
  changes: OutlineProposalChange[]
): OutlineProposalChange[] {
  const result: OutlineProposalChange[] = [];
  const updateIndexBySection = new Map<string, number>();

  for (const change of changes) {
    if (
      change.op !== "update" ||
      typeof change.section_id !== "string" ||
      !change.section_id ||
      !change.section ||
      typeof change.section !== "object"
    ) {
      result.push(change);
      continue;
    }

    const existingIdx = updateIndexBySection.get(change.section_id);
    if (existingIdx === undefined) {
      updateIndexBySection.set(change.section_id, result.length);
      result.push({
        op: "update",
        section_id: change.section_id,
        section: { ...change.section }
      });
      continue;
    }

    const existing = result[existingIdx];
    existing.section = {
      ...(existing.section ?? {}),
      ...change.section
    };
  }

  return result;
}

export function mergeProposalSummaries(summaries: string[]): string {
  const unique = [...new Set(summaries.map((s) => s.trim()).filter(Boolean))];
  if (unique.length === 0) return "Proposed outline updates";
  if (unique.length === 1) return unique[0];
  return unique.join(" ");
}
